import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * DateInput Component.
 * Form input with formatted MM-DD-YYYY text entry and interactive popover calendar picker.
 * Renders calendar via React Portal to prevent overflow container clipping.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} [props.value=""] - Date string in ISO format ("YYYY-MM-DD") or display format ("MM-DD-YYYY").
 * @param {Function} [props.onChange] - Callback fired on date change with standard synthetic event (`{ target: { value: iso } }`).
 * @param {string} [props.className="form-control"] - Input element CSS class name.
 * @param {Object} [props.style={}] - Custom inline styles for input element.
 * @param {string} [props.placeholder="MM-DD-YYYY"] - Input placeholder text.
 * @param {Function} [props.onComplete] - Callback fired when a valid date is completed (`onComplete(iso)`).
 * @param {number} [props.minYear] - Minimum selectable calendar year.
 * @param {number} [props.maxYear] - Maximum selectable calendar year.
 * @returns {JSX.Element} DateInput wrapper element with portal calendar popup.
 */
export default function DateInput({
  value = "",
  onChange,
  className = "form-control",
  style = {},
  placeholder = "MM-DD-YYYY",
  onComplete,
  minYear,
  maxYear,
  ...rest
}) {
  const currentYear = new Date().getFullYear();
  const MIN_YEAR = minYear || 1920;
  const MAX_YEAR = maxYear || currentYear + 5;

  /* ── helpers ── */
  const isoToDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (!y || !m || !d) return iso;
    return `${m}-${d}-${y}`;
  };
  const displayToIso = (display) => {
    const c = display.replace(/[^\d]/g, "");
    if (c.length !== 8) return "";
    const m = c.slice(0, 2), d = c.slice(2, 4), y = c.slice(4, 8);
    if (isNaN(new Date(`${y}-${m}-${d}`))) return "";
    return `${y}-${m}-${d}`;
  };

  /* ── state ── */
  const [display,  setDisplay]  = useState(isoToDisplay(value));
  const [showCal,  setShowCal]  = useState(false);
  const [calYear,  setCalYear]  = useState(() => parseInt(value?.split("-")[0]) || currentYear);
  const [calMonth, setCalMonth] = useState(() => {
    const m = parseInt(value?.split("-")[1]) - 1;
    return isNaN(m) ? new Date().getMonth() : m;
  });
  // Portal position
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, placement: "below" });

  const prevVal  = useRef(value);
  const wrapRef  = useRef(null);
  const inputRef = useRef(null);

  /* ── sync external value ── */
  useEffect(() => {
    if (value !== prevVal.current) {
      prevVal.current = value;
      setDisplay(isoToDisplay(value));
      if (value) {
        const p = value.split("-");
        const y = parseInt(p[0]), m = parseInt(p[1]) - 1;
        if (!isNaN(y)) setCalYear(y);
        if (!isNaN(m)) setCalMonth(m);
      }
    }
  }, [value]);

  /* ── compute portal position when opening ── */
  const openCalendar = useCallback(() => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement = spaceBelow < 320 ? "above" : "below";
      setPopupPos({
        top: placement === "below"
          ? rect.bottom + window.scrollY + 4
          : rect.top  + window.scrollY - 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 276),
        placement,
      });
    }
    setShowCal(true);
  }, []);

  /* ── close on outside click ── */
  useEffect(() => {
    if (!showCal) return;
    const handler = (e) => {
      const popup = document.getElementById("dateinput-portal-popup");
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        popup && !popup.contains(e.target)
      ) {
        setShowCal(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCal]);

  /* ── reposition on scroll/resize ── */
  useEffect(() => {
    if (!showCal) return;
    const update = () => {
      if (wrapRef.current) {
        const rect = wrapRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const placement = spaceBelow < 320 ? "above" : "below";
        setPopupPos(prev => ({
          ...prev,
          top: placement === "below"
            ? rect.bottom + window.scrollY + 4
            : rect.top  + window.scrollY - 4,
          left: rect.left + window.scrollX,
          placement,
        }));
      }
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [showCal]);

  /* ── fire onChange ── */
  const fireChange = useCallback((iso) => {
    if (onChange) onChange({ target: { value: iso } });
  }, [onChange]);

  /* ── text input handlers ── */
  const handleTextChange = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
    let fmt = digits;
    if (digits.length > 4) fmt = `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4)}`;
    else if (digits.length > 2) fmt = `${digits.slice(0,2)}-${digits.slice(2)}`;
    setDisplay(fmt);
    const iso = displayToIso(fmt);
    if (iso) {
      const p = iso.split("-");
      setCalYear(parseInt(p[0]));
      setCalMonth(parseInt(p[1]) - 1);
      fireChange(iso);
      if (onComplete) onComplete(iso);
    } else if (!fmt) {
      fireChange("");
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      const popup = document.getElementById("dateinput-portal-popup");
      const focusInWrapper = wrapRef.current?.contains(document.activeElement);
      const focusInPopup   = popup?.contains(document.activeElement);
      if (!focusInWrapper && !focusInPopup) {
        setShowCal(false);
      }
      const iso = displayToIso(display);
      if (!iso && display) { setDisplay(""); fireChange(""); }
    }, 150);
  };

  /* ── day click ── */
  const handleDayClick = (day) => {
    const m   = String(calMonth + 1).padStart(2, "0");
    const d   = String(day).padStart(2, "0");
    const iso = `${calYear}-${m}-${d}`;
    setDisplay(`${m}-${d}-${calYear}`);
    fireChange(iso);
    if (onComplete) onComplete(iso);
    setShowCal(false);
    inputRef.current?.focus();
  };

  /* ── build grid ── */
  const buildDays = () => {
    const first       = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  };

  const selectedDay = (() => {
    const iso = displayToIso(display);
    if (!iso) return null;
    const p = iso.split("-");
    if (parseInt(p[0]) === calYear && parseInt(p[1]) - 1 === calMonth)
      return parseInt(p[2]);
    return null;
  })();

  const today = new Date();
  const MONTHS = ["January","February","March","April","May","June",
                  "July","August","September","October","November","December"];
  const MSHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const DSHORT = ["Su","Mo","Tu","We","Th","Fr","Sa"];

  const yearOptions = [];
  for (let y = MAX_YEAR; y >= MIN_YEAR; y--) yearOptions.push(y);

  const cells = buildDays();

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value);
    setCalMonth(newMonth);
    const iso = displayToIso(display);
    if (iso) {
      const p = iso.split("-");
      const maxDay = new Date(calYear, newMonth + 1, 0).getDate();
      const safeDay = Math.min(parseInt(p[2]), maxDay);
      const newIso = `${calYear}-${String(newMonth + 1).padStart(2,"0")}-${String(safeDay).padStart(2,"0")}`;
      setDisplay(isoToDisplay(newIso));
      fireChange(newIso);
      if (onComplete) onComplete(newIso);
    }
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    setCalYear(newYear);
    const iso = displayToIso(display);
    if (iso) {
      const p = iso.split("-");
      const maxDay = new Date(newYear, calMonth + 1, 0).getDate();
      const safeDay = Math.min(parseInt(p[2]), maxDay);
      const newIso = `${newYear}-${String(calMonth + 1).padStart(2,"0")}-${String(safeDay).padStart(2,"0")}`;
      setDisplay(isoToDisplay(newIso));
      fireChange(newIso);
      if (onComplete) onComplete(newIso);
    }
  };

  /* ── calendar popup (rendered via portal) ── */
  const calendarPopup = showCal && createPortal(
    <div
      id="dateinput-portal-popup"
      style={{
        position: "absolute",
        top: popupPos.placement === "below"
          ? popupPos.top
          : undefined,
        bottom: popupPos.placement === "above"
          ? `calc(100vh - ${popupPos.top}px)`
          : undefined,
        left: popupPos.left,
        zIndex: 99999,
        background: "white",
        border: "1px solid var(--gray-3)",
        borderRadius: 10,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        width: 276,
        padding: "10px 10px 8px",
        userSelect: "none",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (calMonth === 0) { setCalMonth(11); setCalYear(y => Math.max(MIN_YEAR, y - 1)); }
            else setCalMonth(m => m - 1);
          }}
          style={navBtn}
        >‹</button>

        <select
          value={calMonth}
          onChange={handleMonthChange}
          onMouseDown={(e) => e.stopPropagation()}
          style={dropStyle}
        >
          {MONTHS.map((name, i) => (
            <option key={i} value={i}>{MSHORT[i]}</option>
          ))}
        </select>

        <select
          value={calYear}
          onChange={handleYearChange}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ ...dropStyle, width: 68 }}
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (calMonth === 11) { setCalMonth(0); setCalYear(y => Math.min(MAX_YEAR, y + 1)); }
            else setCalMonth(m => m + 1);
          }}
          style={navBtn}
        >›</button>
      </div>

      {/* Day-of-week headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 4 }}>
        {DSHORT.map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 10.5, fontWeight: 700, color: "var(--gray-1)", padding: "2px 0" }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const isSel   = day === selectedDay;
          const isToday = day === today.getDate() && calMonth === today.getMonth() && calYear === today.getFullYear();
          return (
            <button
              key={day}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleDayClick(day)}
              style={{
                width: "100%", aspectRatio: "1",
                border: isToday && !isSel ? "1.5px solid var(--primary)" : "none",
                borderRadius: 6,
                background: isSel ? "var(--primary)" : "transparent",
                color: isSel ? "white" : isToday ? "var(--primary)" : "var(--dark)",
                fontWeight: isSel || isToday ? 700 : 400,
                fontSize: 12.5, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.1s",
              }}
              onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "var(--primary-light)"; }}
              onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, borderTop: "1px solid var(--gray-3)", paddingTop: 6 }}>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const d = new Date();
            setCalYear(d.getFullYear());
            setCalMonth(d.getMonth());
            handleDayClick(d.getDate());
          }}
          style={footBtn("#1a7a4a")}
        >Today</button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { setDisplay(""); fireChange(""); setShowCal(false); }}
          style={footBtn("#e03c3c")}
        >Clear</button>
      </div>
    </div>,
    document.body
  );

  /* ── render ── */
  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block", width: "100%" }}>
      <div style={{ position: "relative" }}>
        <input
          {...rest}
          ref={inputRef}
          className={className}
          style={{ fontFamily: "monospace", paddingRight: 32, ...style }}
          value={display}
          onChange={handleTextChange}
          onBlur={handleInputBlur}
          onFocus={openCalendar}
          placeholder={placeholder}
          maxLength={10}
          inputMode="numeric"
          autoComplete="off"
        />
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => { e.preventDefault(); showCal ? setShowCal(false) : openCalendar(); }}
          style={{
            position: "absolute", right: 6, top: "50%",
            transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer",
            padding: "2px 4px", color: "var(--gray-1)",
            fontSize: 15, lineHeight: 1, display: "flex", alignItems: "center",
          }}
        >📅</button>
      </div>
      {calendarPopup}
    </div>
  );
}

const navBtn = {
  background: "var(--gray-4)", border: "none", borderRadius: 6,
  width: 26, height: 26, cursor: "pointer", fontSize: 16, fontWeight: 700,
  color: "var(--dark)", display: "flex", alignItems: "center",
  justifyContent: "center", lineHeight: 1, flexShrink: 0,
};
const dropStyle = {
  flex: 1, fontSize: 12.5, fontWeight: 600,
  border: "1px solid var(--gray-3)", borderRadius: 6,
  padding: "3px 4px", background: "white", color: "var(--dark)",
  cursor: "pointer", outline: "none", height: 28,
};
const footBtn = (color) => ({
  background: "none", border: "none", color,
  fontSize: 11.5, fontWeight: 700, cursor: "pointer",
  padding: "2px 6px", borderRadius: 4,
});
